import { marketSearchApi } from '@envato/foundation-types'
import * as classnames from 'classnames'
import get from 'lodash-es/get'
import * as React from 'react'

import { ILocationParams } from '../../../../common/reducers/reactRouterLocation'
import { commaStyleString } from '../../../../common/utils/formatString'
import SearchLink from '../../../containers/searchLinkContainer'
import * as styles from './filterSoftwareVersion.module.css'
import {
  buildTreeFromFilterItems,
  filterNodeFrom,
  locationParamsForPlatform,
  softwareTypeFromString
} from './util'

interface IProps {
  type: string
  items: marketSearchApi.IFilterOption[]
  locationParams: ILocationParams
  separator?: string
  scrollable?: boolean
}

interface IState {
  tree: marketSearchApi.IFilterTreeNode[]
  selectedSoftwareType: string
  selectedSoftwareVersion: string
  softwareTypeKeysExpanded: any
}

interface ISearchLinkProps {
  node: marketSearchApi.IFilterTreeNode
  active: boolean
  activeClass: string
  locationParams: ILocationParams
}

const SearchLinkWithCount = ({
  node,
  active,
  activeClass,
  locationParams
}: ISearchLinkProps) => {
  const { key, name, count } = node
  return (
    <SearchLink
      locationParams={locationParamsForPlatform(key, locationParams)}
      className={classnames(styles.listItemLink, { [activeClass]: active })}
      hash="content"
    >
      {name}
      <span className={styles.listItemCount}>
        {commaStyleString(count, ',')}
      </span>
    </SearchLink>
  )
}

class FilterSoftwareVersion extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props)

    this.state = this.buildStateFromProps(props, {})
  }

  public UNSAFE_componentWillReceiveProps(nextProps: IProps) {
    const newState = this.buildStateFromProps(
      nextProps,
      this.state.softwareTypeKeysExpanded
    )

    this.setState(newState)
  }

  public render() {
    const { locationParams, items } = this.props

    if (!items.length) {
      return null
    }

    const { tree, selectedSoftwareType, selectedSoftwareVersion } = this.state
    const rootNode = {
      ...filterNodeFrom(items[0]),
      name: 'All Software Versions'
    }

    return (
      <nav className={styles.root} role="tree">
        <SearchLinkWithCount
          node={rootNode}
          active={selectedSoftwareType === ''}
          activeClass={styles.activeSoftwareType}
          locationParams={locationParams}
        />
        <ul className={styles.softwareTypeList} role="tree">
          {tree.map((softwareTypeNode: marketSearchApi.IFilterTreeNode) => (
            <li
              key={softwareTypeNode.key}
              role="treeitem"
              aria-expanded={this.isSoftwareTypeExpanded(softwareTypeNode.key)}
              aria-selected={selectedSoftwareVersion === softwareTypeNode.key}
            >
              <div className={classnames(styles.listItem)}>
                <div
                  className={classnames(styles.iconHitArea)}
                  role="link"
                  aria-label={
                    this.isSoftwareTypeExpanded(softwareTypeNode.key)
                      ? `Collapse ${softwareTypeNode.name} Group`
                      : `Expand ${softwareTypeNode.name} Group`
                  }
                  onClick={() => this.handleIconClick(softwareTypeNode.key)}
                >
                  <span
                    className={classnames(styles.icon, {
                      [styles.iconExpanded]: this.isSoftwareTypeExpanded(
                        softwareTypeNode.key
                      ),
                      [styles.iconHidden]:
                        softwareTypeNode.children.length === 0
                    })}
                    aria-hidden={softwareTypeNode.children.length === 0}
                  >
                    ❯
                  </span>
                </div>
                <SearchLinkWithCount
                  node={softwareTypeNode}
                  active={selectedSoftwareType === softwareTypeNode.key}
                  activeClass={styles.activeSoftwareType}
                  locationParams={locationParams}
                />
              </div>
              <ul
                role="group"
                className={classnames(styles.softwareVersionList, {
                  [styles.softwareVersionListCollapsed]: !this.isSoftwareTypeExpanded(
                    softwareTypeNode.key
                  )
                })}
              >
                {softwareTypeNode.children.map(
                  (softwareVersionNode: marketSearchApi.IFilterTreeNode) => (
                    <li
                      key={softwareVersionNode.key}
                      role="treeitem"
                      aria-selected={
                        selectedSoftwareVersion === softwareVersionNode.key
                      }
                    >
                      <SearchLinkWithCount
                        node={softwareVersionNode}
                        active={
                          selectedSoftwareVersion === softwareVersionNode.key
                        }
                        activeClass={styles.activeSoftwareVersion}
                        locationParams={locationParams}
                      />
                    </li>
                  )
                )}
              </ul>
            </li>
          ))}
        </ul>
      </nav>
    )
  }

  private buildStateFromProps = (
    props: IProps,
    softwareTypeKeysExpanded: any
  ): IState => {
    const { locationParams, items } = props
    const selectedSoftwareVersion = get(locationParams, 'platform', '')
    const selectedSoftwareType = softwareTypeFromString(selectedSoftwareVersion)
    const tree = buildTreeFromFilterItems(items)

    const newSoftwareTypeKeysExpanded = softwareTypeKeysExpanded
    newSoftwareTypeKeysExpanded[selectedSoftwareType] = true

    return {
      tree,
      selectedSoftwareType: softwareTypeFromString(selectedSoftwareVersion),
      selectedSoftwareVersion,
      softwareTypeKeysExpanded: newSoftwareTypeKeysExpanded
    }
  }

  private isSoftwareTypeExpanded = (softwareTypeKey: string): boolean => {
    return this.state.softwareTypeKeysExpanded[softwareTypeKey] === true
  }

  private handleIconClick = (softwareTypeKey: string) => {
    const newSoftwareTypeKeysExpanded = {
      ...this.state.softwareTypeKeysExpanded
    }
    newSoftwareTypeKeysExpanded[softwareTypeKey] =
      newSoftwareTypeKeysExpanded[softwareTypeKey] !== true
    this.setState({
      ...this.state,
      softwareTypeKeysExpanded: newSoftwareTypeKeysExpanded
    })
  }
}

export default FilterSoftwareVersion
